/**
 * Frontoffice authentication context.
 *
 * Wires the shared PharosAuthProvider with:
 * - Frontoffice Firebase config
 * - Email/password sign-in as the primary method
 * - Microsoft and Google SSO as secondary methods
 * - Token exchange against the admin-auth API
 * - 60-minute inactivity timeout + 12-hour max session
 *
 * Exposes useAuth() hook for the rest of the app.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { getAuth, GoogleAuthProvider, OAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { initializeFirebase, PharosAuthProvider, usePharosAuth } from '@klareospace/pharos-lib-front-utils/auth';
import type {
  FirebaseConfig,
  SessionEndedReason,
  SignInStrategies,
  TokenExchangeConfig,
} from '@klareospace/pharos-lib-front-utils/auth';
import { env } from '&/config';
import { resolveImpersonationToken } from '&/lib/impersonation-token';
import { queryClient } from '&/lib/query-client';

// --- Firebase initialization ---

const firebaseConfig: FirebaseConfig = {
  apiKey: env.firebaseApiKey,
  appId: env.firebaseAppId,
  authDomain: env.firebaseAuthDomain,
  messagingSenderId: env.firebaseMessagingSenderId,
  projectId: env.firebaseProjectId,
  storageBucket: env.firebaseStorageBucket,
};

const firebaseApp = initializeFirebase(firebaseConfig, 'frontoffice');

// --- Exchange config ---

const exchangeConfig: TokenExchangeConfig = {
  exchangeUrl: `${env.apiBaseUrl}/admin-auth`,
};

// --- SSO providers ---

const microsoftProvider = new OAuthProvider('microsoft.com');
const googleProvider = new GoogleAuthProvider();

/** Inactivity timeout: 60 minutes (PHA-46) */
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const signInStrategies: SignInStrategies = useMemo(
    () => ({
      email: async (email: string, password: string): Promise<void> => {
        const auth = getAuth(firebaseApp);
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch {
          throw new Error('auth.errors.invalidCredentials');
        }
      },
      google: async (): Promise<void> => {
        const auth = getAuth(firebaseApp);
        await signInWithPopup(auth, googleProvider);
      },
      microsoft: async (): Promise<void> => {
        const auth = getAuth(firebaseApp);
        await signInWithPopup(auth, microsoftProvider);
      },
      resetPassword: async (email: string): Promise<void> => {
        try {
          await fetch(`${env.apiBaseUrl}/admin-auth/users/request-password-reset`, {
            body: JSON.stringify({ email }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
        } catch {
          // Swallow network errors — always resolve to avoid leaking outcome differences
        }
      },
    }),
    [],
  );

  return (
    <PharosAuthProvider
      exchangeConfig={exchangeConfig}
      firebaseApp={firebaseApp}
      inactivityTimeoutMs={INACTIVITY_TIMEOUT_MS}
      onSignOut={(): void => {
        queryClient.clear();
        sessionStorage.removeItem('pharos:master-scope');
      }}
      readImpersonationToken={resolveImpersonationToken}
      signInStrategies={signInStrategies}
    >
      {children}
    </PharosAuthProvider>
  );
}

// --- Re-export useAuth with frontoffice-friendly API ---

export interface AuthContextValue {
  error: string | null;
  getToken: () => Promise<string | null>;
  isAuthenticated: boolean;
  /** Whether the current session is an impersonation session */
  isImpersonating: boolean;
  isLoading: boolean;
  /** Notify the auth provider that an API request was sent (resets inactivity timer) */
  notifyActivity: () => void;
  /** Send password reset email via the backend API */
  sendPasswordResetEmail: (email: string) => Promise<void>;
  /** Reason the session status should be shown (null if there is no session status message to show) */
  sessionEndedReason: SessionEndedReason | null;
  /** Sign in with email and password */
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Sign in with Google SSO popup */
  signInWithGoogle: () => Promise<void>;
  /** Sign in with Microsoft SSO popup */
  signInWithMicrosoft: () => Promise<void>;
  /** Sign out and clear tokens */
  signOut: () => Promise<void>;
  user: null | { email: string; name: string };
}

export function useAuth(): AuthContextValue {
  const pharosAuth = usePharosAuth();
  return useMemo(
    () => ({
      error: pharosAuth.error,
      getToken: pharosAuth.getToken,
      isAuthenticated: pharosAuth.isAuthenticated,
      isImpersonating: pharosAuth.isImpersonating,
      isLoading: pharosAuth.isLoading,
      notifyActivity: pharosAuth.notifyActivity,
      sendPasswordResetEmail: pharosAuth.sendPasswordResetEmail!,
      sessionEndedReason: pharosAuth.sessionEndedReason,
      signInWithEmail: pharosAuth.signInWithEmail!,
      signInWithGoogle: pharosAuth.signInWithGoogle!,
      signInWithMicrosoft: pharosAuth.signInWithMicrosoft!,
      signOut: pharosAuth.signOut,
      user: pharosAuth.user,
    }),
    [pharosAuth],
  );
}
